/**
 * What you own.
 *
 * The payoff the whole reward loop has been promising. Until this existed,
 * typing `!tomo` in chat produced a numbered collectible that nobody could
 * open and nobody could look at.
 *
 * Wrapped items come FIRST and are the only thing with a button, because the
 * one action on this screen is opening them. Everything below is the
 * collection itself.
 */
import { useState, type ReactNode } from 'react';
import type { Instance } from '@tomo/contracts';
import { Shell } from '../components/Shell.jsx';
import { Badge, Button, Empty, Failed, Spinner } from '../components/primitives.jsx';
import { ApiError } from '../lib/api.js';
import { instances } from '../lib/tomo.js';
import { useAsync } from '../useAsync.js';
import { useSession } from '../Session.jsx';

export function Collection({ channelId }: { channelId?: string | undefined }): ReactNode {
  const session = useSession();
  const [result, retry] = useAsync((signal) => instances.mine(channelId, signal), [channelId]);
  const [opening, setOpening] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const open = (item: Instance): void => {
    setOpening(item.id);
    setError(null);
    instances.reveal(item.channel_id, item.id).then(
      () => { setOpening(null); retry(); },
      (err: unknown) => {
        setOpening(null);
        setError(err instanceof ApiError ? err.message : 'Could not open that.');
      }
    );
  };

  const nameOf = (id: string) =>
    session.channels.find((c) => c.id === id)?.display_name ?? id;

  return (
    <Shell channelId={channelId}>
      <h1 className="text-xl font-semibold tracking-tight">Your collection</h1>
      <p className="mt-1 text-sm text-ink-400">
        {channelId ? nameOf(channelId) : 'Everything you own, across every channel.'}
      </p>

      {result.state === 'loading' ? <Spinner label="Loading your collection…" /> : null}

      {result.state === 'failed' ? (
        <div className="mt-8">
          <Failed
            message="Could not load your collection."
            detail={result.error.message}
            onRetry={retry}
          />
        </div>
      ) : null}

      {error ? (
        <div className="mt-6"><Failed message={error} /></div>
      ) : null}

      {result.state === 'ready' ? (
        <Ready items={result.value} opening={opening} onOpen={open} nameOf={nameOf} />
      ) : null}
    </Shell>
  );
}

function Ready(
  { items, opening, onOpen, nameOf }:
  {
    items: Instance[];
    opening: string | null;
    onOpen: (i: Instance) => void;
    nameOf: (id: string) => string;
  }
): ReactNode {
  if (items.length === 0) {
    return (
      <div className="mt-8">
        <Empty
          title="Nothing yet."
          hint="Items you earn in a channel show up here. Type the channel’s command in its chat to get started."
        />
      </div>
    );
  }

  const wrapped = items.filter((i) => i.reveal === 'wrapped');
  const open = items.filter((i) => i.reveal !== 'wrapped');

  return (
    <div className="mt-8 space-y-10">
      {wrapped.length > 0 ? (
        <section>
          <h2 className="text-sm font-medium uppercase tracking-wide text-ink-400">
            Unopened · {wrapped.length}
          </h2>
          <ul className="mt-3 grid gap-3 sm:grid-cols-2">
            {wrapped.map((item) => (
              <li
                key={item.id}
                className="flex items-center gap-4 rounded-lg border border-brand-400/30 bg-brand-500/5 p-4"
              >
                <Wrap />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-ink-50">Sealed</span>
                  <span className="block truncate text-xs text-ink-400">
                    {nameOf(item.channel_id)}
                  </span>
                </span>
                <Button onClick={() => { onOpen(item); }} disabled={opening === item.id}>
                  {opening === item.id ? 'Opening…' : 'Open'}
                </Button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section>
        <h2 className="text-sm font-medium uppercase tracking-wide text-ink-400">
          Collected · {open.length}
        </h2>
        {open.length === 0 ? (
          <p className="mt-3 text-sm text-ink-400">
            Nothing opened yet. Open one above to see what is inside.
          </p>
        ) : (
          <ul className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {open.map((item) => <Card key={item.id} item={item} channel={nameOf(item.channel_id)} />)}
          </ul>
        )}
      </section>
    </div>
  );
}

function Card({ item, channel }: { item: Instance; channel: string }): ReactNode {
  return (
    <li className="rounded-lg border border-ink-800 p-4">
      <div className="flex items-start justify-between gap-3">
        <span className="min-w-0">
          <span className="block truncate font-mono text-xs text-ink-400">{item.reward_id}</span>
          <span className="block truncate text-xs text-ink-700">{channel}</span>
        </span>
        <Edition edition={item.edition} />
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {item.custody === 'locked' ? <Badge tone="warn">In a trade</Badge> : null}
        {item.provenance.transfer_count > 0 ? (
          // Provenance is part of what makes a copy a collectible, so it is on
          // the card rather than hidden behind a detail view.
          <Badge>{item.provenance.transfer_count} previous owner
            {item.provenance.transfer_count === 1 ? '' : 's'}</Badge>
        ) : (
          <Badge tone="good">First owner</Badge>
        )}
      </div>
    </li>
  );
}

/**
 * `#3 of 50` and an unnumbered copy are different things, not the same thing
 * with a missing number (L8) — so an uncapped run says so in words rather
 * than rendering an empty space where a number would be.
 */
function Edition({ edition }: { edition: Instance['edition'] }): ReactNode {
  if (edition.number === null) return <Badge>Unnumbered</Badge>;
  return (
    <span className="shrink-0 text-right">
      <span className="block text-lg tabular-nums text-ink-50">#{edition.number}</span>
      {edition.of !== null ? (
        <span className="block text-xs text-ink-400">of {edition.of}</span>
      ) : null}
    </span>
  );
}

function Wrap(): ReactNode {
  return (
    <span
      aria-hidden
      className="grid size-10 shrink-0 place-items-center rounded-md border border-brand-400/40 bg-brand-500/10 text-brand-300"
    >
      ◆
    </span>
  );
}
